import React from 'react';
import { Card, Field, TextField, CssColorField, SaveButton, useSection } from '../AdminUI';
import { Switch } from '../../ui/switch';
import { Plus, Trash2 } from 'lucide-react';
import MediaUpload, { isVideoSrc } from '../MediaUpload';

export default function HeroSection() {
  const [local, setLocal, save] = useSection('heroSlides');
  const [heroEnabled, setHeroEnabled, saveEnabled] = useSection('heroEnabled');
  const update = (i, k, v) => setLocal(local.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const remove = (i) => setLocal(local.filter((_, idx) => idx !== i));
  const add = () => setLocal([...local, { id: Date.now(), title: 'NEW SLIDE', subtitle: 'Edit this banner', bg: 'linear-gradient(135deg, #1a0a1a 0%, #4a0e3e 60%, #8b1a6b 100%)', image: '', cta: 'CLICK', link: '#' }]);

  return (
    <>
    <Card title="Slider Visibility" desc="Turn the hero slider on/off. When off, the section is completely hidden on the site." footer={<SaveButton onClick={saveEnabled} />}>
      <div className="flex items-center gap-3" data-testid="hero-enabled-row">
        <Switch checked={heroEnabled !== false} onCheckedChange={(v) => setHeroEnabled(v)} data-testid="hero-enabled-toggle" />
        <span className="text-sm text-zinc-400">{heroEnabled !== false ? 'Slider is visible' : 'Slider is hidden'}</span>
      </div>
    </Card>
    <Card title="Hero Slides" desc="Auto-rotating banner. Upload image / GIF / video — videos autoplay & loop." footer={<SaveButton onClick={save} />}>
      <div className="space-y-3">
        {(local || []).map((s, i) => (
          <div key={s.id} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4" data-testid={`hero-slide-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-zinc-300 text-sm font-semibold">Slide #{i + 1}</div>
              <button onClick={() => remove(i)} className="h-8 px-3 rounded-md bg-red-600/15 text-red-300 text-xs border border-red-900/40 flex items-center gap-1">
                <Trash2 size={12} /> Remove
              </button>
            </div>
            <div className="mb-3">
              <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Slide Media (image / GIF / video — replaces gradient)</label>
              <MediaUpload value={s.image} onChange={(v) => update(i, 'image', v)} accept="media" previewClass="h-20 w-36" testid={`hero-media-${i}`} />
              {isVideoSrc(s.image) && <p className="text-[11px] text-emerald-400 mt-1">Detected video — will autoplay muted on loop.</p>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Title"><TextField value={s.title} onChange={(v) => update(i, 'title', v)} /></Field>
              <Field label="Subtitle"><TextField value={s.subtitle} onChange={(v) => update(i, 'subtitle', v)} /></Field>
              <Field label="CTA Button Text (leave empty to hide)"><TextField value={s.cta} onChange={(v) => update(i, 'cta', v)} /></Field>
              <Field label="Link"><TextField value={s.link} onChange={(v) => update(i, 'link', v)} /></Field>
              <Field label="Fallback Gradient / Color (used when no media)">
                <CssColorField value={s.bg} onChange={(v) => update(i, 'bg', v)} placeholder="linear-gradient(...)" />
              </Field>
              <div className="flex items-center gap-2 mt-6">
                <input id={`hideText-${i}`} type="checkbox" checked={!!s.hideText} onChange={(e) => update(i, 'hideText', e.target.checked)} className="w-4 h-4 accent-[color:var(--gold)]" data-testid={`hero-hideText-${i}`} />
                <label htmlFor={`hideText-${i}`} className="text-zinc-200 text-sm font-semibold cursor-pointer">Hide all text on this slide (media-only)</label>
              </div>
            </div>
          </div>
        ))}
        <button onClick={add} className="w-full h-10 rounded-md border border-dashed border-zinc-700 hover:border-[color:var(--gold)]/50 text-zinc-300 text-sm font-semibold flex items-center justify-center gap-1.5" data-testid="hero-add-btn">
          <Plus size={14} /> Add Slide
        </button>
      </div>
    </Card>
    </>
  );
}
